/**
 * Marketing Attribution Tracking
 *
 * Captures UTM parameters, referrer, landing page, and ad platform click IDs
 * when users first arrive on the site. Data is stored in a 30-day cookie
 * and attached to orders at checkout for attribution analysis.
 *
 * Supports:
 * - UTM parameters (source, medium, campaign, term, content)
 * - Google Ads (gclid)
 * - Microsoft/Bing Ads (msclkid)
 * - Meta/Facebook Ads (fbclid)
 * - Referrer URL
 * - Landing page path
 */

// Cookie name for attribution data
const ATTRIBUTION_COOKIE_NAME = 'gf_attribution';

// Cookie expiry in days
const ATTRIBUTION_COOKIE_DAYS = 30;

/**
 * Attribution data structure
 */
export interface AttributionData {
  // UTM Parameters
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;

  // Ad Platform Click IDs
  gclid?: string; // Google Ads
  msclkid?: string; // Microsoft/Bing Ads
  fbclid?: string; // Meta/Facebook Ads

  // Context
  referrer?: string;
  landing_page?: string;

  // Timestamp (when attribution was first captured)
  first_touch?: number;
}

/**
 * Check if we're running in the browser
 */
function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

/**
 * Set a cookie with expiry
 */
function setCookie(name: string, value: string, days: number): void {
  if (!isBrowser()) return;

  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

  // Set cookie with secure flags
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

/**
 * Get a cookie value
 */
function getCookie(name: string): string | null {
  if (!isBrowser()) return null;

  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i].trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }

  return null;
}

/**
 * Extract the domain from a referrer URL
 * e.g., "https://www.google.com/search?q=test" -> "google.com"
 */
function extractReferrerDomain(referrer: string): string {
  try {
    const url = new URL(referrer);
    // Remove 'www.' prefix if present
    return url.hostname.replace(/^www\./, '');
  } catch {
    return referrer;
  }
}

/**
 * Check if referrer is from our own site (internal navigation)
 */
function isInternalReferrer(referrer: string): boolean {
  if (!referrer) return true;

  try {
    const url = new URL(referrer);
    const hostname = url.hostname.toLowerCase();

    // Check if it's our domain or a Vercel preview
    return (
      hostname === 'gearfocus.com' ||
      hostname === 'gearfocus.com' ||
      hostname.endsWith('.vercel.app') ||
      hostname === 'localhost'
    );
  } catch {
    return false;
  }
}

/**
 * Capture attribution data from the current page
 * Only captures on first touch (doesn't overwrite existing attribution)
 */
export function captureAttribution(): void {
  if (!isBrowser()) return;

  // Check if we already have attribution data
  const existing = getAttribution();
  if (existing && existing.first_touch) {
    // Already have attribution, don't overwrite
    return;
  }

  // Get URL parameters
  const params = new URLSearchParams(window.location.search);

  // Extract UTM parameters
  const utm_source = params.get('utm_source') || undefined;
  const utm_medium = params.get('utm_medium') || undefined;
  const utm_campaign = params.get('utm_campaign') || undefined;
  const utm_term = params.get('utm_term') || undefined;
  const utm_content = params.get('utm_content') || undefined;

  // Extract ad platform click IDs
  const gclid = params.get('gclid') || undefined;
  const msclkid = params.get('msclkid') || undefined;
  const fbclid = params.get('fbclid') || undefined;

  // Get referrer (only if external)
  let referrer: string | undefined;
  if (document.referrer && !isInternalReferrer(document.referrer)) {
    referrer = extractReferrerDomain(document.referrer);
  }

  // Get landing page path
  const landing_page = window.location.pathname;

  // Build attribution object
  const attribution: AttributionData = {
    ...(utm_source && { utm_source }),
    ...(utm_medium && { utm_medium }),
    ...(utm_campaign && { utm_campaign }),
    ...(utm_term && { utm_term }),
    ...(utm_content && { utm_content }),
    ...(gclid && { gclid }),
    ...(msclkid && { msclkid }),
    ...(fbclid && { fbclid }),
    ...(referrer && { referrer }),
    landing_page,
    first_touch: Date.now(),
  };

  // Only save if we have some meaningful data
  // (at minimum we always have landing_page and first_touch)
  saveAttribution(attribution);
}

/**
 * Save attribution data to cookie
 */
function saveAttribution(data: AttributionData): void {
  if (!isBrowser()) return;

  try {
    const json = JSON.stringify(data);
    setCookie(ATTRIBUTION_COOKIE_NAME, json, ATTRIBUTION_COOKIE_DAYS);
  } catch (e) {
    console.error('[Attribution] Failed to save:', e);
  }
}

/**
 * Get attribution data from cookie
 */
export function getAttribution(): AttributionData | null {
  if (!isBrowser()) return null;

  try {
    const cookie = getCookie(ATTRIBUTION_COOKIE_NAME);
    if (!cookie) return null;

    return JSON.parse(cookie) as AttributionData;
  } catch (e) {
    console.error('[Attribution] Failed to parse:', e);
    return null;
  }
}

/**
 * Get attribution data formatted for Stripe metadata
 * Stripe metadata values must be strings and max 500 chars each
 */
export function getAttributionForStripe(): Record<string, string> {
  const data = getAttribution();
  if (!data) return {};

  const metadata: Record<string, string> = {};

  if (data.utm_source) metadata.utm_source = data.utm_source.substring(0, 500);
  if (data.utm_medium) metadata.utm_medium = data.utm_medium.substring(0, 500);
  if (data.utm_campaign)
    metadata.utm_campaign = data.utm_campaign.substring(0, 500);
  if (data.utm_term) metadata.utm_term = data.utm_term.substring(0, 500);
  if (data.utm_content)
    metadata.utm_content = data.utm_content.substring(0, 500);
  if (data.gclid) metadata.gclid = data.gclid.substring(0, 500);
  if (data.msclkid) metadata.msclkid = data.msclkid.substring(0, 500);
  if (data.fbclid) metadata.fbclid = data.fbclid.substring(0, 500);
  if (data.referrer) metadata.referrer = data.referrer.substring(0, 500);
  if (data.landing_page)
    metadata.landing_page = data.landing_page.substring(0, 500);
  if (data.first_touch)
    metadata.first_touch = data.first_touch.toString();

  return metadata;
}

/**
 * Clear attribution data (useful for testing)
 */
export function clearAttribution(): void {
  if (!isBrowser()) return;

  // Set cookie to expire immediately
  document.cookie = `${ATTRIBUTION_COOKIE_NAME}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
}

/**
 * Get a human-readable summary of attribution source
 * Used for Slack notifications
 */
export function getAttributionSummary(data: AttributionData | null): string {
  if (!data) return 'No attribution data';

  const parts: string[] = [];

  // Source / Medium
  if (data.utm_source || data.utm_medium) {
    const source = data.utm_source || '(not set)';
    const medium = data.utm_medium || '(not set)';
    parts.push(`${source} / ${medium}`);
  }

  // Infer source from click IDs if no UTM
  if (!data.utm_source) {
    if (data.gclid) {
      parts.push('Google Ads (gclid detected)');
    } else if (data.msclkid) {
      parts.push('Bing Ads (msclkid detected)');
    } else if (data.fbclid) {
      parts.push('Meta/Facebook (fbclid detected)');
    } else if (data.referrer) {
      parts.push(`Referrer: ${data.referrer}`);
    } else {
      parts.push('Direct / Unknown');
    }
  }

  return parts.join(' | ');
}
